/**
 * 页面适配工具
 * 平板三种尺寸：960x600、1000x600、1130x706，进页面根据当前视口动态匹配最近的设计稿尺寸
 * 适配方案：使用 scale 缩放，保持设计稿宽高比，居中显示
 */

// 平板预设设计稿尺寸（进页面时根据视口动态选取其一）
const DESIGN_PRESETS = [
  [960, 600],
  [1000, 600],
  [1130, 706],
]

// 当前使用的设计稿尺寸（由 pickDesignSizeFromViewport 或 setDesignSize 更新）
let designWidth = 1000
let designHeight = 600

// 防抖函数，避免频繁计算
let adaptTimer = null
const ADAPT_DEBOUNCE_MS = 100

/**
 * 根据当前视口从预设中选取最匹配的设计稿尺寸（距离和最小）
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @returns {[number, number]} [width, height]
 */
function pickDesignSizeFromViewport(viewportWidth, viewportHeight) {
  let best = DESIGN_PRESETS[0]
  let minDist = Infinity
  for (const [pw, ph] of DESIGN_PRESETS) {
    const dist = Math.abs(viewportWidth - pw) + Math.abs(viewportHeight - ph)
    if (dist < minDist) {
      minDist = dist
      best = [pw, ph]
    }
  }
  return best
}

/**
 * 初始化页面适配
 */
export function initPageAdapt() {
  const adapt = () => {
    // 清除之前的定时器
    if (adaptTimer) {
      clearTimeout(adaptTimer)
    }

    adaptTimer = setTimeout(() => {
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // 进页面动态获取：根据当前视口从三种平板尺寸中选取最匹配的设计稿
      const [w, h] = pickDesignSizeFromViewport(viewportWidth, viewportHeight)
      designWidth = w
      designHeight = h

      const appElement = document.getElementById('app')

      // 计算宽高缩放比例（取较小值，确保内容完整显示且不变形）
      const scaleX = viewportWidth / designWidth
      const scaleY = viewportHeight / designHeight
      const scale = Math.min(scaleX, scaleY)

      // 计算缩放后的实际尺寸
      const scaledWidth = designWidth * scale
      const scaledHeight = designHeight * scale

      // 计算居中偏移量（以缩放后的尺寸为基准）
      const offsetX = (viewportWidth - scaledWidth) / 2
      const offsetY = (viewportHeight - scaledHeight) / 2

      if (appElement) {
        // 设置根容器尺寸（固定为当前设计稿尺寸）
        appElement.style.width = `${designWidth}px`
        appElement.style.height = `${designHeight}px`
        appElement.style.transformOrigin = 'top left'
        // 使用 scale 缩放，并通过 translate 居中
        // translate 的单位是相对于原始尺寸的，所以需要除以 scale
        appElement.style.transform = `scale(${scale}) translate(${offsetX / scale}px, ${offsetY / scale}px)`
        appElement.style.position = 'absolute'
        appElement.style.top = '0'
        appElement.style.left = '0'
      }

      // 设置 body 和 html 样式，确保全屏显示
      document.body.style.width = `${viewportWidth}px`
      document.body.style.height = `${viewportHeight}px`
      document.body.style.overflow = 'hidden'
      document.body.style.margin = '0'
      document.body.style.padding = '0'
      document.body.style.position = 'relative'

      document.documentElement.style.width = `${viewportWidth}px`
      document.documentElement.style.height = `${viewportHeight}px`
      document.documentElement.style.overflow = 'hidden'

      // 打印适配信息（开发环境）
      if (process.env.NODE_ENV === 'development') {
        console.log('📐 页面适配信息:', {
          设计稿尺寸: `${designWidth}x${designHeight}`,
          视口尺寸: `${viewportWidth}x${viewportHeight}`,
          缩放比例: scale.toFixed(4),
          缩放后尺寸: `${scaledWidth.toFixed(2)}x${scaledHeight.toFixed(2)}`,
          居中偏移: `(${offsetX.toFixed(2)}px, ${offsetY.toFixed(2)}px)`,
        })
      }
    }, ADAPT_DEBOUNCE_MS)
  }

  // 确保 #app 元素存在后再执行适配
  const tryAdapt = () => {
    const appElement = document.getElementById('app')
    if (appElement) {
      adapt()
    } else {
      // 如果 #app 还不存在，延迟重试
      setTimeout(tryAdapt, 50)
    }
  }

  // 等待 DOM 加载完成后再初始化适配
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryAdapt)
  } else {
    // DOM 已经加载完成，尝试执行（如果 #app 不存在会重试）
    tryAdapt()
  }

  // 监听窗口大小变化
  window.addEventListener('resize', adapt)
  // 监听方向变化（移动设备横竖屏切换）
  window.addEventListener('orientationchange', () => {
    setTimeout(adapt, 150)
  })

  // 返回清理函数（如果需要）
  return () => {
    if (adaptTimer) {
      clearTimeout(adaptTimer)
    }
    window.removeEventListener('resize', adapt)
    window.removeEventListener('orientationchange', adapt)
    document.removeEventListener('DOMContentLoaded', tryAdapt)
  }
}

/**
 * 动态设置设计稿尺寸（会触发一次重新适配）
 * @param {number} width - 设计稿宽度
 * @param {number} height - 设计稿高度
 */
export function setDesignSize(width, height) {
  if (Number.isFinite(width) && width > 0) designWidth = width
  if (Number.isFinite(height) && height > 0) designHeight = height
  window.dispatchEvent(new Event('resize'))
}

/**
 * 获取当前使用的设计稿尺寸
 * @returns {{ width: number, height: number }}
 */
export function getDesignSize() {
  return { width: designWidth, height: designHeight }
}

/**
 * 获取当前缩放比例
 */
export function getCurrentScale() {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const scaleX = viewportWidth / designWidth
  const scaleY = viewportHeight / designHeight
  return Math.min(scaleX, scaleY)
}

/**
 * 将设计稿尺寸转换为实际像素（用于动态计算）
 * @param {number} size - 设计稿中的尺寸
 * @returns {number} - 实际像素值
 */
export function pxToReal(size) {
  return size * getCurrentScale()
}

